/**
 * Allergikorreksjonen (Pattern 6 / D-03) — teksten som settes inn i samtalen når
 * allergifilteret stopper en runde.
 *
 * Delt fordi begge sider leser den: API-et skriver den inn i `ai_messages`, og
 * `hydrateThread.ts` i weben må kjenne den igjen for å skjule den når en lagret
 * samtale lastes inn på nytt.
 *
 * #136 F1: Anthropic krever at en user-melding følger det avviste
 * assistent-utkastet, så korreksjonen må ha rollen `user`. Tidligere sto den
 * uten merking, og modellen svarte den som om brukeren hadde anklaget den
 * («Jeg har ikke foreslått noen ingredienser …») og glemte selve oppgaven.
 * Nå er den merket som en systemmelding og ber om den opprinnelige
 * forespørselen; `ai.service.ts` legger i tillegg til en systemblokk som
 * forklarer markøren. Filteret skanner neste runde som før — dette er
 * ordlyd, ikke sikkerhet.
 */

/** Første tekst i hver korreksjonsmelding. Ikke endre uten å beholde den gamle i `isAllergyCorrectionPrompt`. */
export const ALLERGY_CORRECTION_MARKER = '[Systemmelding fra Stell – ikke fra brukeren]';

/** Ordlyden før #136, som fortsatt ligger i lagrede samtaler. */
const LEGACY_PREFIX = 'Forrige svar nevnte ';
const LEGACY_SUFFIX = 'Skriv på nytt uten dette ingrediensvalget.';

export function buildCorrectionPrompt(
  hits: readonly { synonym: string }[],
  /**
   * Hvem allergenet tilhører. Chat skanner hele husstanden, så det er ikke
   * nødvendigvis den som spurte — send eierne av `hits[0].allergen`.
   */
  allergicNames: string | readonly string[],
): string {
  const synonym = hits[0]?.synonym ?? 'en allergen';
  const names = typeof allergicNames === 'string' ? [allergicNames] : [...allergicNames];

  // Bokmål: "A er allergisk mot" (ent.) vs "A og B er allergiske mot" (fl.).
  const owner =
    names.length === 0
      ? 'noen i familien er allergisk mot'
      : names.length === 1
        ? `${names[0]} er allergisk mot`
        : `${names.slice(0, -1).join(', ')} og ${names[names.length - 1]} er allergiske mot`;

  return (
    `${ALLERGY_CORRECTION_MARKER} Forrige utkast ble stoppet av allergifilteret fordi det nevnte ` +
    `«${synonym}», som ${owner}. Fullfør brukerens opprinnelige forespørsel på nytt, med de ` +
    `verktøykallene den trenger, uten å nevne «${synonym}». Svar brukeren direkte, og ikke ` +
    `kommenter denne meldingen eller allergifilteret.`
  );
}

/** Sann for korreksjonsmeldinger, både dagens og den gamle ordlyden. */
export function isAllergyCorrectionPrompt(text: string): boolean {
  return (
    text.startsWith(ALLERGY_CORRECTION_MARKER) ||
    (text.startsWith(LEGACY_PREFIX) && text.includes(LEGACY_SUFFIX))
  );
}
